import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Save, CopyPlus, Share2, Sparkles, RotateCcw, Eye, Loader2, Users, Copy } from "lucide-react";
import { SaveAsDialog, ShareDialog } from "@/components/common/Dialogs";
import { AiRecommendationSheet } from "@/components/common/AiRecommendationSheet";
import { fmtDate } from "@/lib/format";

/**
 * Standard header for every tool: title, editable calc name, actions (save / save as / share / AI / reset).
 */
export function ToolHeader({ icon: Icon, eyebrow, title, description, calc, getSummary, tool, getAiContext, extraActions }) {
  const { meta, setTitle, setShares, saving, dirty, readOnly, save, saveAs, duplicate, reset } = calc;
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const isOwner = meta.permission === "owner";

  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col xl:flex-row xl:items-end gap-4 justify-between">
        <div className="min-w-0">
          <div className="eyebrow flex items-center gap-2">
            {Icon && <Icon className="h-3.5 w-3.5" />} {eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1.5 text-foreground" data-testid="tool-page-title">
            {title}
          </h1>
          {description && <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl leading-relaxed">{description}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="tool-actions">
          {extraActions}
          {!readOnly && (
            <Button onClick={() => save(getSummary())} disabled={saving} className="press" data-testid="tool-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
            </Button>
          )}
          <Button variant="secondary" onClick={() => setSaveAsOpen(true)} className="press border border-border" data-testid="tool-save-as-button">
            <CopyPlus className="h-4 w-4" /> Simpan Sebagai
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="secondary"
                  onClick={() => setShareOpen(true)}
                  disabled={!isOwner}
                  className="press border border-border"
                  data-testid="tool-share-button"
                >
                  <Share2 className="h-4 w-4" /> Bagikan
                  {meta.shares?.length > 0 && <span className="num text-[11px] text-primary">({meta.shares.length})</span>}
                </Button>
              </span>
            </TooltipTrigger>
            {!isOwner && <TooltipContent className="text-xs">Hanya pemilik yang bisa membagikan</TooltipContent>}
          </Tooltip>
          <Button
            onClick={() => setAiOpen(true)}
            className="press bg-[hsl(43_30%_10%)] text-primary border border-[color:var(--hairline)] hover:bg-[hsl(43_35%_14%)]"
            data-testid="ai-recommendation-open-button"
          >
            <Sparkles className="h-4 w-4" /> Rekomendasi AI
          </Button>
          <Button variant="ghost" onClick={() => setResetOpen(true)} className="press text-muted-foreground" data-testid="tool-reset-button">
            <RotateCcw className="h-4 w-4" /> Baru
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
        <span className="text-xs text-muted-foreground shrink-0">Judul perhitungan</span>
        <Input
          value={meta.title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={readOnly}
          className="h-9 flex-1 min-w-[200px] bg-[hsl(220_12%_8%)] border-border font-medium"
          data-testid="tool-calc-title-input"
        />
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {meta.id ? (
            <Badge variant="outline" className="border-border text-muted-foreground" data-testid="tool-calc-status">
              {dirty ? "Ada perubahan belum disimpan" : `Tersimpan · ${fmtDate(meta.updated_at)}`}
            </Badge>
          ) : (
            <Badge variant="outline" className="border-border text-muted-foreground" data-testid="tool-calc-status">
              Belum disimpan
            </Badge>
          )}
          {meta.shares?.length > 0 && isOwner && (
            <Badge variant="outline" className="border-[color:var(--hairline)] text-[hsl(43_60%_66%)]">
              <Users className="h-3 w-3 mr-1" /> Dibagikan ke {meta.shares.length}
            </Badge>
          )}
          {!isOwner && meta.owner_email && (
            <Badge variant="outline" className="border-[color:var(--hairline)] text-[hsl(43_60%_66%)]" data-testid="tool-calc-owner-badge">
              Milik {meta.owner_email} · {meta.permission === "edit" ? "Bisa edit" : "Lihat saja"}
            </Badge>
          )}
        </div>
      </div>

      {readOnly && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_8%)] px-4 py-3"
          data-testid="tool-readonly-banner"
        >
          <Eye className="h-4 w-4 text-primary" />
          <span className="text-sm flex-1">
            Anda membuka perhitungan ini dalam mode <b>lihat saja</b>. Duplikat untuk mengubah dan menyimpan versi Anda sendiri.
          </span>
          <Button size="sm" onClick={duplicate} disabled={saving} data-testid="tool-duplicate-button">
            <Copy className="h-4 w-4" /> Duplikat ke milik saya
          </Button>
        </div>
      )}

      <SaveAsDialog
        open={saveAsOpen}
        onOpenChange={setSaveAsOpen}
        initialTitle={meta.title}
        saving={saving}
        onConfirm={(t) => saveAs(t, getSummary())}
      />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} calcId={meta.id} shares={meta.shares} onSaved={setShares} />
      <AiRecommendationSheet open={aiOpen} onOpenChange={setAiOpen} tool={tool} getContext={getAiContext} calcId={meta.id} />
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Mulai perhitungan baru?</AlertDialogTitle>
            <AlertDialogDescription>
              Form akan dikembalikan ke nilai awal. {dirty ? "Perubahan yang belum disimpan akan hilang." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tool-reset-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={reset} data-testid="tool-reset-confirm">
              Ya, mulai baru
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
